import {
  AgXToneMapping,
  Box3,
  Color,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  PMREMGenerator,
  PointLight,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";

import { createArtworkLabel } from "./artwork-label";
import type { MuseumContent } from "./contracts";
import { validateCollisionBody, type ColliderKind, type CollisionBody } from "./collision";
import type { RoutePassage } from "./route";

export type MuseumScene = {
  scene: Group;
  camera: PerspectiveCamera;
  paintings: ReadonlyMap<string, Object3D>;
  labels: ReadonlyMap<string, Object3D>;
  routeStops: ReadonlyMap<string, Vector3>;
  colliders: readonly CollisionBody[];
  roomFloors: ReadonlyMap<string, Box3>;
  corridorFloor: Box3 | null;
  passage: RoutePassage | null;
  roomFillCount: number;
};

/**
 * Give the gallery floors a polished finish that reflects the room around them.
 *
 * A planar reflector would render the scene a second time — the wrong trade on the
 * tablet and kiosk hardware this has to run on. A generated room environment costs
 * one small prefilter at load and nothing per frame, and on a low-roughness floor it
 * reads the way a real museum's poured stone does.
 *
 * Safe to skip entirely: without it the scene renders exactly as before.
 */
export function polishFloors(scene: Group, renderer: WebGLRenderer): () => void {
  const pmrem = new PMREMGenerator(renderer);
  const environment = pmrem.fromScene(new RoomEnvironment(), 0.04);

  // Applied per material rather than scene-wide, so only the floors pick up the
  // reflection and the walls stay the matte plaster the lighting design assumes.
  scene.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    if (object.userData.kind !== "floor") return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (!(material instanceof MeshStandardMaterial)) continue;
      // Not a mirror: enough gloss to catch the room, enough roughness to stay walkable.
      material.envMap = environment.texture;
      material.roughness = Math.min(material.roughness, 0.22);
      material.metalness = Math.max(material.metalness, 0.35);
      material.envMapIntensity = 0.55;
      material.needsUpdate = true;
    }
  });

  pmrem.dispose();
  return () => {
    environment.texture.dispose();
  };
}

export async function parseMuseumScene(
  glb: ArrayBuffer,
  assetBaseUrl: string,
  content: MuseumContent,
): Promise<MuseumScene> {
  const loader = new GLTFLoader();
  const gltf = await loader.parseAsync(glb, assetBaseUrl);
  const scene = gltf.scene;
  scene.name = scene.name || "VM_MUSEUM";
  scene.updateMatrixWorld(true);
  const paintings = new Map<string, Object3D>();
  const labelAnchors = new Map<string, Object3D>();
  const routeStops = new Map<string, Vector3>();
  const routeStopOrders = new Map<string, number>();
  const colliders: CollisionBody[] = [];
  const roomFloors = new Map<string, Box3>();
  const visitorCameras: PerspectiveCamera[] = [];
  const roomFillNames = new Set<string>();
  let corridorFloor: Box3 | undefined;
  let previewCameras = 0;

  scene.traverse((object) => {
    if (object.name.startsWith("camera_preview")) previewCameras += 1;
    if (object.name === "camera_visitor_spawn" && object instanceof PerspectiveCamera) {
      visitorCameras.push(object);
    }
    if (object.userData.interactive === true) {
      const id = requiredString(object, "artwork_id");
      if (object.name !== `artwork_${id}` || paintings.has(id)) {
        throw new Error(`GLB_ARTWORK_IDENTITY_INVALID: ${object.name}`);
      }
      paintings.set(id, object);
    }
    if (object.name.startsWith("label_anchor_")) {
      const id = requiredString(object, "artwork_id");
      if (object.name !== `label_anchor_${id}` || labelAnchors.has(id)) {
        throw new Error(`GLB_LABEL_IDENTITY_INVALID: ${object.name}`);
      }
      labelAnchors.set(id, object);
    }
    if (object.name.startsWith("route_stop_")) {
      const id = requiredString(object, "artwork_id");
      const order = requiredNumber(object, "route_order");
      if (object.name !== `route_stop_${String(order).padStart(2, "0")}_${id}`) {
        throw new Error(`GLB_ROUTE_IDENTITY_INVALID: ${object.name}`);
      }
      if (routeStops.has(id)) throw new Error(`GLB_ROUTE_IDENTITY_INVALID: ${object.name}`);
      routeStops.set(id, object.getWorldPosition(new Vector3()));
      routeStopOrders.set(id, order);
    }
    if (object.userData.collider === true) {
      const kind = requiredColliderKind(object);
      const body = { name: object.name, kind, bounds: new Box3().setFromObject(object) };
      validateCollisionBody(body);
      colliders.push(body);
      if (kind === "floor" && typeof object.userData.room_id === "string") {
        roomFloors.set(object.userData.room_id, body.bounds.clone());
      }
      if (object.name === "VM_CORRIDOR_floor") corridorFloor = body.bounds.clone();
    }
    if (object.userData.lighting_role === "room_fill" && object instanceof PointLight) {
      roomFillNames.add(object.name);
    }
  });

  if (
    paintings.size !== content.artworks.length ||
    !content.artworks.every((artwork) => paintings.has(artwork.id)) ||
    labelAnchors.size !== content.artworks.length ||
    !content.artworks.every((artwork) => labelAnchors.has(artwork.id)) ||
    routeStops.size !== content.route.length ||
    !content.route.every(
      (stop) => routeStops.has(stop.artwork_id) && routeStopOrders.get(stop.artwork_id) === stop.order,
    ) ||
    colliders.length !== (content.rooms.length === 1 ? 5 : 18) ||
    roomFillNames.size !== content.rooms.length ||
    !content.rooms.every((room) => roomFillNames.has(`fill_${room.id}`)) ||
    previewCameras !== 0 ||
    visitorCameras.length !== 1
  ) {
    throw new Error("GLB_SCENE_CONTRACT_MISMATCH: identidad o conteos inesperados");
  }
  for (const artwork of content.artworks) {
    const object = paintings.get(artwork.id);
    if (
      object?.name !== artwork.object_name ||
      object.userData.room_id !== artwork.room_id ||
      object.userData.route_order !== artwork.route_order
    ) {
      throw new Error(`GLB_CONTENT_MISMATCH: ${artwork.id}`);
    }
  }
  const camera = visitorCameras[0];
  if (camera === undefined) throw new Error("GLB_CAMERA_MISSING: camera_visitor_spawn");
  const worldPosition = camera.getWorldPosition(new Vector3());
  const worldQuaternion = camera.getWorldQuaternion(camera.quaternion.clone());
  scene.attach(camera);
  camera.position.copy(worldPosition);
  camera.quaternion.copy(worldQuaternion);
  camera.near = Math.min(camera.near, 0.1);
  camera.far = Math.max(camera.far, 50);
  camera.updateProjectionMatrix();
  const helpers = new Group();
  helpers.name = "VM_VIEWER_HELPERS";
  scene.add(helpers);
  const labels = new Map<string, Object3D>();
  for (const artwork of content.artworks) {
    const painting = paintings.get(artwork.id);
    const anchor = labelAnchors.get(artwork.id);
    if (painting === undefined || anchor === undefined) {
      throw new Error(`GLB_LABEL_MISSING: ${artwork.id}`);
    }
    painting.userData.interactive = false;
    const label = createArtworkLabel(artwork, painting, anchor);
    helpers.add(label);
    labels.set(artwork.id, label);
  }
  scene.updateMatrixWorld(true);
  if (
    roomFloors.size !== content.rooms.length ||
    (content.rooms.length === 2 && corridorFloor === undefined) ||
    (content.rooms.length === 1 && corridorFloor !== undefined)
  ) {
    throw new Error("GLB_PASSAGE_CONTRACT_MISMATCH");
  }
  return {
    scene,
    camera,
    paintings,
    labels,
    routeStops,
    colliders,
    roomFloors,
    corridorFloor: corridorFloor?.clone() ?? null,
    passage: corridorFloor === undefined ? null : derivePassage(corridorFloor, roomFloors),
    roomFillCount: roomFillNames.size,
  };
}

export function createRenderer(canvas: HTMLCanvasElement): WebGLRenderer {
  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: false });
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = AgXToneMapping;
  renderer.toneMappingExposure = 0.003;
  renderer.setClearColor(new Color(0x303638), 1);
  const pixelRatioLimit = window.matchMedia("(pointer: coarse)").matches ? 1.25 : 2;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, pixelRatioLimit));
  return renderer;
}

export function artworkTextureImage(painting: Object3D): CanvasImageSource {
  let image: unknown;
  painting.traverse((object) => {
    if (image !== undefined || !(object instanceof Mesh)) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      const map = (material as unknown as { map?: { image?: unknown } }).map;
      if (isCanvasImageSource(map?.image)) {
        image = map.image;
        break;
      }
    }
  });
  if (!isCanvasImageSource(image)) throw new Error(`GLB_ARTWORK_TEXTURE_MISSING: ${painting.name}`);
  return image;
}

function isCanvasImageSource(value: unknown): value is CanvasImageSource {
  return (
    value instanceof HTMLImageElement ||
    value instanceof HTMLCanvasElement ||
    (typeof ImageBitmap !== "undefined" && value instanceof ImageBitmap) ||
    (typeof OffscreenCanvas !== "undefined" && value instanceof OffscreenCanvas)
  );
}

function derivePassage(
  corridorFloor: Box3,
  roomFloors: ReadonlyMap<string, Box3>,
): RoutePassage {
  const size = corridorFloor.getSize(new Vector3());
  const axis = size.x >= size.z ? "x" : "z";
  const crossAxis = axis === "x" ? "z" : "x";
  const rooms = [...roomFloors.entries()].sort(
    (left, right) =>
      left[1].getCenter(new Vector3())[axis] - right[1].getCenter(new Vector3())[axis],
  );
  const minRoom = rooms[0];
  const maxRoom = rooms[1];
  if (minRoom === undefined || maxRoom === undefined) throw new Error("GLB_ROOM_FLOORS_INVALID");
  const center = corridorFloor.getCenter(new Vector3());
  return {
    axis,
    min: corridorFloor.min[axis],
    max: corridorFloor.max[axis],
    crossAxisValue: center[crossAxis],
    minRoomId: minRoom[0],
    maxRoomId: maxRoom[0],
  };
}

export function currentRoom(position: Vector3, floors: ReadonlyMap<string, Box3>): string {
  for (const [roomId, bounds] of floors) {
    if (
      position.x >= bounds.min.x &&
      position.x <= bounds.max.x &&
      position.z >= bounds.min.z &&
      position.z <= bounds.max.z
    ) {
      return roomId;
    }
  }
  return "corridor";
}

function requiredString(object: Object3D, key: string): string {
  const value: unknown = object.userData[key];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`GLB_EXTRA_INVALID: ${object.name}.${key}`);
  }
  return value;
}

function requiredNumber(object: Object3D, key: string): number {
  const value: unknown = object.userData[key];
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new Error(`GLB_EXTRA_INVALID: ${object.name}.${key}`);
  }
  return value;
}

function requiredColliderKind(object: Object3D): ColliderKind {
  const value: unknown = object.userData.structure_kind;
  if (value === "wall" || value === "lintel" || value === "floor" || value === "ceiling") {
    return value;
  }
  throw new Error(`GLB_EXTRA_INVALID: ${object.name}.structure_kind`);
}
